import { useMemo, useState } from "react";
import LoadingSkeletonUntyped from "@shared/LoadingSkeleton";
import type { ReactElement } from "react";
import commaFormat from "@utils/commaFormat";
import { RESULTS_PAGE_SIZE, RESULTS_PAGE_SIZE_OPTIONS } from "../constants";
import { getMatchedCellTint, isRowUnresolved } from "../utils";
import type { BulkMatchRow, MatchCandidate } from "../types";
import CompanyNameWithFlag from "./CompanyNameWithFlag";
import ConfidenceChip from "./ConfidenceChip";
import MatchedAgainstCell from "./MatchedAgainstCell";
import MatchRowAction from "./MatchRowAction";

const LoadingSkeleton = LoadingSkeletonUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

type MatchResultsTableProps = {
  rows: BulkMatchRow[];
  onCandidateSelected: (rowId: string, candidate: MatchCandidate) => void;
  isProcessing?: boolean;
};

const MatchResultsTable = ({ rows, onCandidateSelected, isProcessing = false }: MatchResultsTableProps) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(RESULTS_PAGE_SIZE);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = useMemo(() => rows.slice(currentPage * pageSize, currentPage * pageSize + pageSize), [rows, currentPage, pageSize]);
  const rangeStart = rows.length ? currentPage * pageSize + 1 : 0;
  const rangeEnd = Math.min(rows.length, currentPage * pageSize + pageSize);

  if (!rows.length) {
    return <div className="tw-py-16 tw-text-center tw-text-sm tw-text-gray-500">No companies match this filter.</div>;
  }

  return (
    <div className="tw-mx-auto tw-max-w-[1336px] ">
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-table-fixed tw-border-collapse tw-text-sm">
          <thead>
            <tr className="tw-h-10 tw-border-y tw-border-table-border tw-bg-white">
              <th className="tw-px-3 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-900">Uploaded company</th>
              <th className="tw-px-3 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-900">Matched against</th>
              <th className="tw-w-24 tw-px-3 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-900">Confidence</th>
              <th className="tw-w-48 tw-px-3 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-900">Review</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const rowTint = getMatchedCellTint(row.confidence);
              return (
                <tr key={row.row_id} className="tw-min-h-14 tw-border-b tw-border-table-border tw-align-top">
                  <td className="tw-px-3 tw-py-2">
                    <CompanyNameWithFlag name={row.uploaded_company_name} country={row.uploaded_country} countryCode={row.uploaded_country_code} />
                  </td>
                  {isProcessing && isRowUnresolved(row) ? (
                    <>
                      <td className="tw-px-3 tw-py-4">
                        <LoadingSkeleton width="55%" height="14px" />
                        <div className="tw-mt-2">
                          <LoadingSkeleton width="30%" height="12px" />
                        </div>
                      </td>
                      <td className="tw-px-3 tw-py-4">
                        <div className="tw-mx-auto tw-w-12">
                          <LoadingSkeleton width="100%" height="22px" />
                        </div>
                      </td>
                      <td className="tw-px-3 tw-py-4">
                        <LoadingSkeleton width="70%" height="14px" />
                      </td>
                    </>
                  ) : (
                    <>
                      <td className={`tw-p-0 ${rowTint}`}>
                        <MatchedAgainstCell row={row} />
                      </td>
                      <td className={`tw-px-3 tw-py-4 ${rowTint}`}>
                        <ConfidenceChip confidence={row.confidence} />
                      </td>
                      <td className={`tw-px-3 tw-py-4  ${rowTint}`}>
                        <MatchRowAction row={row} onCandidateSelected={onCandidateSelected} />
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-table-border tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-600">
        <span>Result per page</span>
        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          className="tw-mr-2 tw-border-none tw-bg-transparent tw-text-sm tw-font-medium tw-text-gray-900 focus:tw-outline-none">
          {RESULTS_PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className="tw-mx-2">
          {rangeStart}-{rangeEnd} of {commaFormat(rows.length)}
        </span>
        <button type="button" aria-label="Previous page" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-300 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:hover:tw-bg-white">
          <span className="material-symbols-outlined tw-text-lg tw-leading-none tw-text-gray-500">chevron_left</span>
        </button>
        <button type="button" aria-label="Next page" disabled={currentPage >= pageCount - 1} onClick={() => setPage(currentPage + 1)} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-300 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:hover:tw-bg-white">
          <span className="material-symbols-outlined tw-text-lg tw-leading-none tw-text-gray-500">chevron_right</span>
        </button>
      </div>
    </div>
  );
};

export default MatchResultsTable;
